import { type FC, useEffect, useMemo, useRef } from 'react';
import {
  type AddSamplePlanContext,
  isSampleData,
  type SampleData,
  useAddSamplePlanContext,
  usePlannedSample,
} from '@/components/Samples/AddSamplePlanProvider';
import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import Select, { type SingleValue } from 'react-select';
import styles from './AddSamplePlanGroup.module.scss';
import { type SampleDetailType, type SampleDetailUnit, type TempSampleTypeId } from '@/model/SamplePlan.model';
import { type TeamGlossaryApiId } from '@/model/Glosary.model';
import RemoveButton from '@/components/UI/RemoveButton';
import { RiAlertFill } from 'react-icons/ri';
import { isCurrentSampleArchived } from '@/components/Samples/Samples.utils.tsx';

type SampleOption = AddSamplePlanContext['sampleOptions'] extends Map<any, infer V> ? V : never;

const detailUnits: Record<SampleDetailType, Array<{ value: SampleDetailUnit; label: SampleDetailUnit }>> = {
  weight: [
    { value: 'µg', label: 'µg' },
    { value: 'mg', label: 'mg' },
    { value: 'g', label: 'g' },
  ],
  volume: [
    { value: 'µl', label: 'µl' },
    { value: 'ml', label: 'ml' },
    { value: 'cm³', label: 'cm³' },
    { value: 'mm³', label: 'mm³' },
  ],
  width: [{ value: 'mm', label: 'mm' }],
  length: [{ value: 'mm', label: 'mm' }],
  depth: [{ value: 'mm', label: 'mm' }],
} as const;

const selectStyles = {
  menu: (base: any) => ({ ...base, zIndex: 9999 }),
};

export const SampleTypeSelector: FC<{ type: TeamGlossaryApiId | TempSampleTypeId }> = ({ type }) => {
  const { sampleOptions, sampleGlossary, samplePlanState } = useAddSamplePlanContext();

  const sampleData = usePlannedSample(type);
  const sampleDetails = useMemo(
    () =>
      _notNil(sampleData) && isSampleData(sampleData)
        ? (sampleGlossary.get(sampleData.type)?.options?.details as Array<SampleDetailType>)
        : [],
    [sampleGlossary, sampleData]
  );

  const hasFilledDetailsRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isSampleData(sampleData) || !_isNotEmpty(sampleDetails)) {
      return;
    }

    if (hasFilledDetailsRef.current === sampleData.type) {
      return;
    }

    const missingDetails = sampleDetails.filter(
      (detail) => !_notNil(sampleData[detail]) && _notNil(detailUnits[detail]) && detailUnits[detail].length > 0
    );

    if (missingDetails.length === 0) {
      return;
    }

    const update = missingDetails.reduce<SampleData>(
      (acc, detail) => ({ ...acc, [detail]: detailUnits[detail][0].value }),
      { ...sampleData }
    );

    const frameId = window.requestAnimationFrame(() => {
      hasFilledDetailsRef.current = sampleData.type;
      samplePlanState.setSampleData(update, undefined, 'user');
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [sampleData, sampleDetails, samplePlanState]); // Removed 'type' - redundant

  const typeSelectorDisabled = useMemo<boolean>(() => {
    // Disable the selector once a sample type has been selected (not temp type)
    return isSampleData(sampleData);
  }, [sampleData]);

  const unitSelectionDisabled = useMemo<boolean>(() => {
    if (!isSampleData(sampleData)) {
      return false;
    }
    if (isCurrentSampleArchived(sampleData, sampleGlossary)) {
      return true;
    }
    return sampleData.definitions.some((def) => {
      const counts = def.collected_samples_count;
      return _notNil(counts) && (counts.totals.planned > 0 || counts.totals.collected > 0);
    });
  }, [sampleData, sampleGlossary]);

  const availableSampleOptions = useMemo(() => {
    // Filter out archived sample types from the dropdown
    return Array.from(sampleOptions.values()).filter((option) => {
      const glossaryEntry = sampleGlossary.get(option.value);
      return _isNil(glossaryEntry?.archived_at);
    });
  }, [sampleOptions, sampleGlossary]);

  const handleSampleTypeChange = (option: SingleValue<SampleOption>) => {
    if (_notNil(option) && sampleGlossary.has(option.value) && _notNil(type)) {
      samplePlanState.updateSampleType(type, option.value, sampleGlossary);
    }
  };

  const handleSampleDetailUnitChange = (
    detail: SampleDetailType,
    option: SingleValue<{
      label: SampleDetailUnit;
      value: SampleDetailUnit;
    }>
  ) => {
    if (_notNil(option?.value) && _notNil(sampleData) && isSampleData(sampleData)) {
      samplePlanState.updateSampleDetailUnit(sampleData.type, detail, option.value);
    }
  };

  return (
    <div
      className="flex flex-row justify-between items-start"
      data-test-component="SampleTypeSelector"
      data-test-element="container"
    >
      <div className="flex flex-row">
        <div className={`flex flex-column ${styles.selectContainer}`} data-test-element="sample-type-select">
          <label>Sample type</label>
          <Select
            className="w5 ui__select f6"
            data-testId="sample-type-select"
            classNamePrefix="ui__select"
            placeholder="Select sample type"
            isDisabled={typeSelectorDisabled}
            isSearchable
            onChange={handleSampleTypeChange}
            isOptionDisabled={(options) => samplePlanState.plannedSamplesHasKey(options.value)}
            value={_notNil(sampleData) && isSampleData(sampleData) ? sampleOptions.get(sampleData.type) : undefined}
            options={availableSampleOptions}
            styles={selectStyles}
          />
        </div>
        {isSampleData(sampleData) &&
          _isNotEmpty(sampleDetails) &&
          sampleDetails.map((detail) => (
            <div
              className={`flex flex-column ml2 ${styles.selectContainer}`}
              key={detail}
              data-test-element="detail-select"
              data-test-key={detail}
              data-tooltip-id="global-tooltip-id"
              data-tooltip-content={
                unitSelectionDisabled
                  ? isCurrentSampleArchived(sampleData, sampleGlossary)
                    ? 'Units cannot be changed for archived sample types'
                    : 'Units cannot be changed when planned or collected samples exist'
                  : undefined
              }
            >
              <label>{`${detail.charAt(0).toUpperCase()}${detail.slice(1)} units`}</label>
              <Select
                className="w4 ui__select f6"
                data-testId={`${detail}-select`}
                classNamePrefix="ui__select"
                onChange={(option) => handleSampleDetailUnitChange(detail, option)}
                options={detailUnits[detail]}
                value={detailUnits[detail].find((opt) => opt.value === sampleData[detail])}
                isDisabled={detailUnits[detail].length === 1 || unitSelectionDisabled}
                isSearchable
                styles={selectStyles}
              />
            </div>
          ))}
      </div>
      <div className="flex items-center">
        {isCurrentSampleArchived(sampleData, sampleGlossary) && (
          <div className="mr3 flex items-center" data-testid="archived-sample-warning">
            <RiAlertFill className="yellow" size={25} />
            <span className="pl2">Archived Sample Type</span>
          </div>
        )}
        <RemoveButton onClick={() => _notNil(type) && samplePlanState.removeSampleData(type)} />
      </div>
    </div>
  );
};
